import { useEffect, useMemo, useRef, useState } from "react";
import { Gem, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CompanyAvatar } from "@/components/deals/company-avatar";
import { cn } from "@/lib/utils";
import { getErrorMessage } from "@/lib/api-error";
import { usePremiumDeals } from "@/hooks/use-premium-deals";
import type { PremiumDeal } from "@/api/org-billing-api";

interface PremiumSwapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId?: string;
  /** The deal being promoted, named in the copy so the swap is unambiguous. */
  newDealName: string;
  isSubmitting: boolean;
  onConfirm: (demoteApplicationId: string) => void | Promise<void>;
}

const byRecencyDesc = (a: PremiumDeal, b: PremiumDeal) => {
  const ta = a.last_activity_at ? Date.parse(a.last_activity_at) : 0;
  const tb = b.last_activity_at ? Date.parse(b.last_activity_at) : 0;
  return tb - ta;
};

export function PremiumSwapModal({
  open,
  onOpenChange,
  orgId,
  newDealName,
  isSubmitting,
  onConfirm,
}: PremiumSwapModalProps) {
  const { deals, isLoading, isError, error, refetch } = usePremiumDeals(
    orgId,
    open
  );
  const [selected, setSelected] = useState<string | null>(null);
  const initializedRef = useRef(false);

  const sortedDeals = useMemo(() => [...deals].sort(byRecencyDesc), [deals]);

  // Default to the least-recently-active premium deal (last in the list) — the
  // most likely one the user is done with — then re-arm on close.
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setSelected(null);
      return;
    }
    if (initializedRef.current || sortedDeals.length === 0) return;
    setSelected(sortedDeals[sortedDeals.length - 1].application_id);
    initializedRef.current = true;
  }, [open, sortedDeals]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Premium slot full</DialogTitle>
          <DialogDescription>
            You&apos;re using all your premium deal slots. Choose one to free up
            — it keeps its existing reports (view-only); new analysis moves to{" "}
            <span className="font-medium text-foreground">{newDealName}</span>.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <p className="text-sm text-destructive">
              {getErrorMessage(error, "Couldn't load your premium deals.")}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : sortedDeals.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No premium deals found.
          </p>
        ) : (
          <ScrollArea className="max-h-[320px] pr-3">
            <ul className="space-y-1" role="radiogroup" aria-label="Premium deals">
              {sortedDeals.map((deal) => {
                const checked = selected === deal.application_id;
                return (
                  <li key={deal.application_id}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      onClick={() => setSelected(deal.application_id)}
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-3 rounded-md border p-3 text-left transition-colors",
                        checked
                          ? "border-foreground bg-muted/40"
                          : "border-border hover:bg-muted/30"
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                          checked ? "border-foreground" : "border-border"
                        )}
                      >
                        {checked && (
                          <span className="h-2 w-2 rounded-full bg-foreground" />
                        )}
                      </span>
                      <CompanyAvatar
                        websiteUrl={deal.website}
                        alt={deal.deal_name}
                        className="h-7 w-7 shrink-0 bg-background ring-1 ring-border"
                        iconClassName="h-3.5 w-3.5"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {deal.deal_name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {deal.workspace_name}
                        </span>
                      </span>
                      <Gem
                        className={cn(
                          "h-3.5 w-3.5 shrink-0",
                          checked ? "text-foreground" : "text-muted-foreground"
                        )}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => selected && onConfirm(selected)}
            disabled={isSubmitting || isLoading || isError || !selected}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Freeing up…
              </>
            ) : (
              "Free up & generate"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
